import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ArrowLeft, Calendar, User } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api, mediaUrl } from "@/lib/api";

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

// Per-article SEO overrides (keyed by slug) for hand-tuned metadata.
const SEO_OVERRIDES = {
  "gst-update-for-manufacturers-2026": {
    title: "Automated GST & Vendor Verification in India | IndiaBusinessCircle",
    description:
      "Learn how manual GST verification and vendor due diligence slow Indian B2B trade, and how automated credential checks eliminate the trust gap for MSMEs.",
    keywords:
      "automated GST verification, B2B vendor onboarding India, MSME credential check, GSTIN verification API, Indian B2B trade network, HSN code validation, vendor due diligence India, digital compliance ecosystem, supply chain friction",
  },
};

export default function NewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/news/${slug}`)
      .then(({ data }) => active && setArticle(data))
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading)
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-brand" size={32} />
        </div>
      </Layout>
    );

  if (notFound || !article)
    return (
      <Layout>
        <div className="min-h-[50vh] flex flex-col items-center justify-center text-center">
          <p className="font-display font-bold text-xl text-slate-800">Article not found</p>
          <Link to="/news" className="mt-4 text-brand font-semibold">
            ← Back to News
          </Link>
        </div>
      </Layout>
    );

  const seo = SEO_OVERRIDES[article.slug] || {};
  const pageTitle = seo.title || `${article.title} | IndiaBusinessCircle`;
  const metaDescription = seo.description || article.excerpt;
  const canonical = `${window.location.origin}/news/${article.slug}`;
  const cover = mediaUrl(article.cover_url);

  return (
    <Layout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {cover && <meta property="og:image" content={cover} />}
      </Helmet>

      <article className="mx-auto max-w-3xl px-5 sm:px-8 py-10">
        <Link to="/news" data-testid="news-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-brand transition-colors">
          <ArrowLeft size={15} /> All News
        </Link>

        <span className="mt-6 block text-xs font-bold uppercase tracking-[0.2em] text-brand">{article.category}</span>
        <h1 data-testid="article-title" className="mt-3 font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
          {article.title}
        </h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {formatDate(article.created_at)}</span>
          <span className="inline-flex items-center gap-1.5"><User size={14} /> {article.author_name}</span>
        </div>

        {cover && (
          <div className="mt-8 rounded-2xl overflow-hidden bg-slate-100">
            <img src={cover} alt={article.title} className="w-full object-cover max-h-[420px]" />
          </div>
        )}

        <div data-testid="article-content" className="mt-8 space-y-5 text-lg leading-relaxed text-slate-700">
          {(article.content || "").split(/\n{2,}|\n/).filter((p) => p.trim()).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </article>
    </Layout>
  );
}
